import type { ComponentType } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Activity, FileClock, FileSearch, FileText } from "lucide-react";
import { toast } from "sonner";

type PatientReport = {
  id: string;
  title: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
  route: string;
  isAvailable: boolean;
};

const patientReports: PatientReport[] = [
  {
    id: "health-report",
    title: "Relatório Detalhado Saúde",
    description: "Visualização completa e detalhada de todos os dados de sua saúde com a possibilidade de selecionar os conteúdos a serem exibidos e compartilhamento via WhatsApp e PDF com seu profissional da saúde.",
    icon: Activity,
    route: "/paciente/relatorios/saude",
    isAvailable: true,
  },
  {
    id: "exam-evolution",
    title: "Evolução de Exames",
    description: "Acompanhamento comparativo de resultados laboratoriais por período.",
    icon: FileSearch,
    route: "",
    isAvailable: false,
  },
  {
    id: "timeline-report",
    title: "Linha do Tempo Clínica",
    description: "Resumo cronológico de eventos, consultas e registros de saúde.",
    icon: FileText,
    route: "",
    isAvailable: false,
  },
  {
    id: "monitoring-report",
    title: "Relatório de Monitoramento",
    description: "Painel consolidado para acompanhamento da sua evolução contínua.",
    icon: FileClock,
    route: "",
    isAvailable: false,
  },
];

export default function RelatoriosPaciente() {
  const navigate = useNavigate();

  const handleOpenReport = (report: PatientReport) => {
    if (!report.isAvailable) {
      toast.info("Este relatório estará disponível em breve.");
      return;
    }

    navigate(report.route);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Relatórios</h1>
        <p className="text-muted-foreground">
          Explore os relatórios disponíveis para acompanhar sua saúde.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {patientReports.map((report) => {
          const Icon = report.icon;

          return (
            <Card
              key={report.id}
              className={`transition-shadow ${
                report.isAvailable
                  ? "cursor-pointer hover:shadow-lg"
                  : "cursor-not-allowed opacity-80"
              }`}
              onClick={() => handleOpenReport(report)}
            >
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="text-lg">{report.title}</CardTitle>
                    </div>
                  </div>
                  <Badge variant={report.isAvailable ? "secondary" : "outline"}>
                    {report.isAvailable ? "Disponível" : "Em breve"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-sm leading-relaxed">
                  {report.description}
                </CardDescription>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
